import React, { useState } from 'react';
import { generalMedia } from '../../data/generalMedia';
import { trackEvent } from '../../firebase/analytics';
import { FamlyEnquiryLink } from '../sections/Contact/FamlyEnquiryLink';

interface NavItem {
  label: string;
  href: string;
  submenu?: NavItem[];
}

const navItems: NavItem[] = [
  { label: 'About us', href: '/about' },
  { label: 'News', href: '/news' },
  { label: 'For Parents & Carers', href: '/parents' },
  { label: 'Meals', href: '/meals' },
  { label: 'Fees', href: '/fees' },
  { label: 'Romford', href: '/romford' },
  { label: 'Tilbury', href: '/tilbury' },
];

const makeNavClick =
  (label: string, href: string, location: 'desktop_nav' | 'mobile_nav' | 'logo' | 'cta') =>
  () => {
    trackEvent({
      name: 'select_content',
      action: 'click',
      label,
      page: typeof window !== 'undefined' ? window.location.pathname : undefined,
      params: {
        destination: href,
        nav_location: location,
      },
    });
  };

const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const logoImg = generalMedia['general-logo'];

  return (
    <nav className="bg-white border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex">
            <div className="flex-shrink-0 flex items-center">
              <a
                href="/"
                onClick={makeNavClick('logo', '/', 'logo')}
                data-analytics="logo"
              >
                <img
                  src={logoImg.src}
                  alt={logoImg.alt}
                  className="h-10 w-auto"
                  loading="lazy"
                  decoding="async"
                  fetchPriority="low"
                  width={865}
                  height={288}
                />
              </a>
            </div>

            <div className="hidden md:ml-6 md:flex md:space-x-8">
              {navItems.map(item => (
                <a
                  key={item.label}
                  href={item.href}
                  onClick={makeNavClick(item.label, item.href, 'desktop_nav')}
                  data-analytics={`nav-${item.label}`}
                  className="inline-flex items-center px-1 pt-1 border-b-2 border-transparent text-sm font-medium text-teal-600 hover:border-teal-400"
                >
                  {item.label}
                </a>
              ))}
            </div>
          </div>

          <div className="flex items-center">
            <div className="hidden md:flex">
              <button
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm bg-purple-600 hover:bg-purple-700"
              >
                <FamlyEnquiryLink
                  ctaLocation="nav_desktop"
                  className="text-white visited:text-white hover:text-white focus:text-white active:text-white no-underline !text-white"
                >
                  Enquire now
                </FamlyEnquiryLink>
              </button>
            </div>

            <div className="flex md:hidden">
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none"
              >
                <span className="sr-only">Open main menu</span>
                {isOpen ? (
                  <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none"
                       viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none"
                       viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {isOpen && (
        <div className="md:hidden">
          <div className="pt-2 pb-3 space-y-1">
            {navItems.map(item => (
              <a
                key={item.label}
                href={item.href}
                onClick={makeNavClick(item.label, item.href, 'mobile_nav')}
                data-analytics={`mobile-${item.label}`}
                className="block pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-300"
              >
                {item.label}
              </a>
            ))}
            <FamlyEnquiryLink
              ctaLocation="nav_mobile"
              className="block w-full text-center mt-2 px-4 py-2 rounded-md shadow-sm
                        bg-purple-600 hover:bg-purple-700
                        text-white visited:!text-white hover:text-white focus:text-white active:text-white
                        no-underline hover:no-underline"
            >
              Enquire now
            </FamlyEnquiryLink>
          </div>

          <div className="border-t border-gray-200 py-3 text-center text-gray-500">
            © {new Date().getFullYear()} Great Child
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;